import type { ContractTemplateListItem } from './contractTemplateUtils'

export type OrgDetails = { org_id: number; org_name: string }

export type OrgGroup = {
  id: string
  name: string
  chip?: string
}

export type ContractGroupingContext = {
  currentOrgId: number
  ownerOrg?: OrgDetails
  sharedWith?: OrgDetails[]
}

export type CurrentOrgForGrouping = {
  id?: number
  name?: string
  connected_orgs?: { org_id: number; org_name: string }[]
}

export type ProjectShareForGrouping = { org_id: number; org_name: string }

export type ContractSelectSection = {
  id: string
  name: string
  chip: string
  contracts: ContractTemplateListItem[]
  hideHeader?: boolean
}

const parseOrgIdFromUrl = (url: string) => {
  const urlKey = '/orgs/'
  return parseInt(url?.substring(url.indexOf(urlKey) + urlKey.length).replace('/', ''), 10)
}

const getChoiceOrgId = (choice: ContractTemplateListItem) => {
  if (choice.org_id != null) return Number(choice.org_id)
  if (typeof choice.org === 'string') return parseOrgIdFromUrl(choice.org)
  return NaN
}

const isChoiceShared = (shareWithOrgs: number[], sharedWith: number[]) => {
  return sharedWith?.every((i) => shareWithOrgs.includes(i))
}

export const findOtherOrgIds = (
  orgId: number,
  ownerId: number | undefined,
  sharedWith: undefined | { org_id: number }[]
) => {
  if (!ownerId || !sharedWith) return []
  if (orgId === ownerId) return sharedWith.map((s) => s.org_id)
  return [ownerId].concat(sharedWith.filter((s) => s.org_id !== orgId).map((s) => s.org_id))
}

export const getOrgGroups = (
  orgId: number,
  ownerOrg: OrgDetails | undefined,
  sharedWith?: OrgDetails[]
): OrgGroup[] => {
  const groups: OrgGroup[] = []

  if (ownerOrg) {
    const allOrgs = [ownerOrg, ...(sharedWith || [])]
    const selfOrg = allOrgs.find((x) => x.org_id === orgId)

    if (selfOrg) {
      groups.push({ id: `${selfOrg.org_id}-shared`, name: selfOrg.org_name || '', chip: 'Shared' })
      groups.push({ id: `${selfOrg.org_id}-unshared`, name: selfOrg.org_name || '', chip: 'Not Shared' })
    }

    for (const item of allOrgs) {
      if (item.org_id !== orgId) {
        groups.push({ id: `${item.org_id}-shared`, name: item.org_name || '', chip: 'Shared' })
        groups.push({ id: `${item.org_id}-unshared`, name: item.org_name || '', chip: 'Not Shared' })
      }
    }
  }

  return groups
}

export const filterByOrgShare = (
  choice: ContractTemplateListItem,
  orgId: number,
  otherOrgs: number[]
): string => {
  const rawShareWithOrgs = choice.share_with_orgs
  const shareWithOrgs =
    Array.isArray(rawShareWithOrgs) && rawShareWithOrgs.length
      ? rawShareWithOrgs.map((x) => (typeof x === 'number' ? x : parseOrgIdFromUrl(String(x))))
      : undefined
  const choiceOrgId = getChoiceOrgId(choice)
  const checkShared = choiceOrgId === orgId ? otherOrgs : [orgId]
  return (
    String(choiceOrgId) +
    (shareWithOrgs && isChoiceShared(shareWithOrgs, checkShared) ? '-shared' : '-unshared')
  )
}

export const checkChoicesForOwnItems = (orgId: number, choices: ContractTemplateListItem[]) => {
  const choiceIds = choices.map((x) => getChoiceOrgId(x))
  return choiceIds?.includes(orgId)
}

export const resolveOwnerOrg = (
  currentOrg: CurrentOrgForGrouping | undefined,
  projectOrgId: number | undefined
): OrgDetails | undefined => {
  if (!projectOrgId) return undefined
  const fromConnected = currentOrg?.connected_orgs?.find((x) => x.org_id === projectOrgId)
  if (fromConnected) {
    return { org_id: fromConnected.org_id, org_name: fromConnected.org_name }
  }
  if (currentOrg?.id === projectOrgId) {
    return { org_id: currentOrg.id, org_name: currentOrg.name || '' }
  }
  return undefined
}

export const buildContractGroupingContext = (input: {
  currentOrgId?: number
  projectOrgId?: number
  currentOrg?: CurrentOrgForGrouping
  sharedWith?: ProjectShareForGrouping[]
}): ContractGroupingContext | undefined => {
  const { currentOrgId, projectOrgId, currentOrg, sharedWith } = input
  if (!sharedWith?.length || currentOrgId == null) return undefined

  const ownerOrg = resolveOwnerOrg(currentOrg, projectOrgId)
  if (!ownerOrg) return undefined

  return {
    currentOrgId,
    ownerOrg,
    sharedWith: sharedWith.map((item) => ({ org_id: item.org_id, org_name: item.org_name })),
  }
}

export const buildContractSelectSections = (
  contracts: ContractTemplateListItem[] | undefined,
  context: ContractGroupingContext | undefined
): ContractSelectSection[] | null => {
  if (!contracts?.length || !context?.sharedWith?.length || !context.ownerOrg) {
    return null
  }

  const { currentOrgId, ownerOrg, sharedWith } = context
  const groups = getOrgGroups(currentOrgId, ownerOrg, sharedWith)
  const otherOrgIds = findOtherOrgIds(currentOrgId, ownerOrg.org_id, sharedWith)

  const groupMap: Record<string, ContractSelectSection> = {}
  for (const group of groups) {
    groupMap[group.id] = {
      id: group.id,
      name: group.name,
      chip: group.chip || '',
      contracts: [],
    }
  }

  for (const choice of contracts) {
    const groupId = filterByOrgShare(choice, currentOrgId, otherOrgIds)
    const section = groupMap[groupId]
    if (section) section.contracts.push(choice)
  }

  const sections: ContractSelectSection[] = []
  for (const group of groups) {
    const section = groupMap[group.id]
    if (section.contracts.length) sections.push(section)
  }

  if (!sections.length) return null

  if (sections.length === 1) {
    const showSoloGroupName =
      !!sharedWith?.length && !checkChoicesForOwnItems(currentOrgId, contracts)
    if (!showSoloGroupName) {
      sections[0] = { ...sections[0], hideHeader: true }
    }
  }

  return sections
}
